import { removeHighIntentOffers } from 'actions/RecommendationActions'
import { HighIntentTileProps } from 'components/OfferList/HighIntent/HighIntentRecommendationTile'
import HighIntentRecommendationTileLoader from 'components/OfferList/HighIntent/HighIntentRecommendationTileLoader'
import HotLeadTile from 'components/OfferList/HighIntent/HotLeadTile'
import RecentlyViewedTile from 'components/OfferList/HighIntent/RecentlyViewedTile'
import { TrackingProps } from 'contexts/trackingContext'
import { useAppDispatch } from 'hooks/reduxHooks'
import { nonNullable } from 'lib/array/arrayUtils'
import { isHotLead, isRecentlyViewed, shouldDisplayAbandonedCartAsHotLead, shouldDisplayAsRecentlyViewed } from 'lib/offer/highIntentOffers'
import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'

export enum HighIntentOfferDisplayStatus {
  LOADING,
  SHOW,
  HIDE
}

interface Props {
  highIntentOffer: App.HighIntentOffer
  position: number
  tracking?: TrackingProps
  onClick?: HighIntentTileProps['onClick']
  onClose?: HighIntentTileProps['onClose']
  className?: string
}

function convertToHotLeadOffer(highIntentOffer: App.HighIntentOffer): App.HotLeadOffer {
  if ('items' in highIntentOffer) {
    const cartItems = highIntentOffer.items as Array<App.Checkout.AnyItem>

    // This is an abandoned cart - extract check-in/out dates and occupancy from cart items
    const firstItem = cartItems[0] as App.Checkout.AccommodationItem | undefined

    // Extract occupancy - handle both single occupancy and array formats
    let occupancy: Array<App.Occupants> = []
    if (firstItem && 'occupancy' in firstItem) {
      const itemOccupancy = firstItem.occupancy
      occupancy = Array.isArray(itemOccupancy) ? itemOccupancy.filter(nonNullable) : [itemOccupancy]
    }

    return {
      offerId: highIntentOffer.offerId,
      offerType: highIntentOffer.offerType,
      category: 'sticky_hot_leads', // Treat as hot lead for display
      creationTime: highIntentOffer.creationTime,
      lereVersion: highIntentOffer.lereVersion,
      isDismissed: highIntentOffer.isDismissed ?? false,
      checkIn: firstItem && 'checkIn' in firstItem ? firstItem.checkIn : undefined,
      checkOut: firstItem && 'checkOut' in firstItem ? firstItem.checkOut : undefined,
      occupancy,
    }
  }
  // Already a HotLeadOffer, ensure occupancy is always an array
  return {
    ...highIntentOffer,
    occupancy: highIntentOffer.occupancy ?? [],
    isDismissed: highIntentOffer.isDismissed ?? false,
    category: 'sticky_hot_leads',
  }
}

function HighIntentOfferCard(props: Props) {
  const { onClose, tracking, highIntentOffer, position, onClick } = props
  const [loadInViewRef, loadInView] = useInView({ triggerOnce: true })
  const [shouldDisplay, setShouldDisplay] = useState(true)

  const forceDisplayAsRecentlyViewed = shouldDisplayAsRecentlyViewed(highIntentOffer)
  const forceDisplayAbandonedCartAsHotLead = shouldDisplayAbandonedCartAsHotLead(highIntentOffer)
  const recentTile = isRecentlyViewed(highIntentOffer) || forceDisplayAsRecentlyViewed
  const hotLeadTile = !forceDisplayAsRecentlyViewed && (isHotLead(highIntentOffer) || forceDisplayAbandonedCartAsHotLead)
  // Never render abandoned cart tile - always use hot lead tile instead
  const abandonedCartTile = false

  const handleDisplayStatusChange = useCallback((status: HighIntentOfferDisplayStatus) => {
    setShouldDisplay(status === HighIntentOfferDisplayStatus.LOADING || status === HighIntentOfferDisplayStatus.SHOW)
  }, [])

  const cannotRender = !shouldDisplay || (!recentTile && !hotLeadTile && !abandonedCartTile)

  const hotLeadOffer = useMemo(() => {
    return hotLeadTile ? convertToHotLeadOffer(highIntentOffer) : undefined
  }, [hotLeadTile, highIntentOffer])

  const dispatch = useAppDispatch()
  // if this offer cannot be rendered (unavailable) we want to remove it from the state
  useEffect(() => {
    if (cannotRender) {
      dispatch(removeHighIntentOffers([highIntentOffer.offerId], [highIntentOffer.category]))
    }
  }, [dispatch, highIntentOffer, cannotRender])

  if (cannotRender) {
    return null
  }

  return <div ref={loadInViewRef}>
    {!loadInView && <HighIntentRecommendationTileLoader />}
    {loadInView && <>
      {recentTile && <RecentlyViewedTile
        position={position}
        offerId={highIntentOffer.offerId}
        highIntentCategory={highIntentOffer.category}
        tracking={tracking}
        key={highIntentOffer.offerId}
        onClose={onClose}
        onClick={onClick}
        onDisplayStatusChange={handleDisplayStatusChange}
      />}
      {hotLeadOffer && <HotLeadTile
        position={position}
        highIntentOffer={hotLeadOffer}
        tracking={tracking}
        key={hotLeadOffer.offerId}
        onClose={onClose}
        onDisplayStatusChange={handleDisplayStatusChange}
      />}
      {/* {abandonedCartTile && <AbandonedCartTile
        position={position}
        highIntentOffer={highIntentOffer}
        tracking={tracking}
        key={highIntentOffer.offerId}
        onClose={onClose}
        onDisplayStatusChange={handleDisplayStatusChange}
      />} */}
    </>}
  </div>
}

export default HighIntentOfferCard
